import type { CardDetail, CardStockUpdate } from "@hobbyshopify/backend-client";
import type { Result } from "operation-result.js";
import { SubtreeController } from "subtree.js/core";
import type { AppDeps } from "../../app-deps";
import { archiveCard, loadCard, repriceCard, setCardFloor, setCardPrice, setCardStock } from "../../core/ops/cards-ops";
import { errorText } from "../../core/primitives/errors";
import { Loader } from "../../core/primitives/loader";
import { can } from "../../core/primitives/store-role";
import { PriceHistoryController } from "../../sections/price-history/price-history-controller";
import { NOTICE_MS } from "../cards/cards-controller";
import type { CardPrices } from "../cards/cards-model";
import { CardDetailActions, type CardDetailDialog, CardDetailState } from "./card-detail-model";

export type CardDetailDeps = Pick<AppDeps, "client" | "authHandler" | "storeRepository">;

export interface CardDetailRouting {
  back(): void;
  toCards(): void;
}

/**
 * One card, with room to read it.
 *
 * The row in the catalogue holds a thumbnail and one line; this is where the
 * images are large, the identity is spelled out field by field, and the lots,
 * the listing and the scans behind the card are visible. The actions are the
 * same ones the row offers, on the same endpoints.
 */
export class CardDetailController extends SubtreeController implements CardDetailActions {
  readonly state = new CardDetailState();
  readonly history: PriceHistoryController;
  readonly #detail: Loader<{ shopId: string; cardId: string }, CardDetail>;
  #shopId: string | null = null;
  #noticeTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    readonly deps: CardDetailDeps,
    readonly cardId: string,
    readonly routing: CardDetailRouting,
  ) {
    super();
    this.subtree.put(CardDetailState, this.state);
    this.subtree.put(CardDetailActions, this);
    this.history = this.own(new PriceHistoryController(this.subtree, deps));
    this.#detail = this.own(
      new Loader({
        status: this.state.status,
        fetch: (o: { shopId: string; cardId: string }) => loadCard({ ...deps, ...o }),
        loaded: (detail) => {
          this.state.detail.value = detail;
        },
      }),
    );

    this.sync(() => {
      const shopId = deps.storeRepository.shopId;
      if (shopId === this.#shopId) return;
      this.#shopId = shopId;
      void this.#reload();
    }, [deps.storeRepository]);
    // Null role (not yet resolved) behaves like today — allowed — until it is known.
    this.sync(() => {
      const role = deps.storeRepository.selectedStore?.myRole ?? null;
      this.state.canEditInventory.value = role === null || can(role, "inventoryEdit");
    }, [deps.storeRepository]);
    this.autoDispose(() => this.#clearNoticeTimer());
  }

  reprice = async () => {
    const shopId = this.#shopId;
    if (shopId === null || this.state.busy.value) return;
    this.state.busy.value = true;
    try {
      const r = await repriceCard({ ...this.deps, shopId, cardId: this.cardId });
      if (this.isDisposed) return;
      if (!r.ok) {
        this.#notify(errorText(r.errors));
        return;
      }
      this.#notify(r.value == null ? "No price found." : "Price refreshed.");
      await this.#reload();
    } finally {
      this.state.busy.value = false;
    }
  };

  openDialog = (dialog: CardDetailDialog) => {
    this.state.dialogError.value = null;
    this.state.dialog.value = dialog;
  };

  closeDialog = () => {
    this.state.dialog.value = null;
  };

  savePrices = async (next: CardPrices) => {
    await this.#runDialog("price", async (shopId) => {
      const priced = await setCardPrice({ ...this.deps, shopId, cardId: this.cardId, priceCents: next.priceCents });
      if (!priced.ok) return priced;
      return setCardFloor({ ...this.deps, shopId, cardId: this.cardId, floorPriceCents: next.floorPriceCents });
    });
  };

  saveStock = async (change: CardStockUpdate) => {
    await this.#runDialog("stock", (shopId) => setCardStock({ ...this.deps, shopId, cardId: this.cardId, change }));
  };

  confirmArchive = async () => {
    await this.#runDialog(
      "archive",
      (shopId) => archiveCard({ ...this.deps, shopId, cardId: this.cardId }),
      () => this.routing.toCards(),
    );
  };

  /** Try, reload and close; or report inside the dialog and stay open. */
  async #runDialog(
    dialog: CardDetailDialog,
    action: (shopId: string) => Promise<Result<unknown, Error>>,
    after?: () => void,
  ): Promise<void> {
    const shopId = this.#shopId;
    if (this.state.dialog.value !== dialog || shopId === null || this.state.busy.value) return;
    this.state.busy.value = true;
    this.state.dialogError.value = null;
    try {
      const r = await action(shopId);
      if (this.isDisposed) return;
      if (!r.ok) {
        this.state.dialogError.value = errorText(r.errors);
        return;
      }
      this.state.dialog.value = null;
      if (after) after();
      else await this.#reload();
    } finally {
      this.state.busy.value = false;
    }
  }

  #notify(text: string): void {
    this.#clearNoticeTimer();
    this.state.notice.value = text;
    this.#noticeTimer = setTimeout(() => {
      this.#noticeTimer = null;
      this.state.notice.value = null;
    }, NOTICE_MS);
  }

  #clearNoticeTimer(): void {
    if (this.#noticeTimer !== null) clearTimeout(this.#noticeTimer);
    this.#noticeTimer = null;
  }

  retry = () => {
    void this.#reload();
  };

  async #reload(): Promise<void> {
    if (this.#shopId === null) return;
    await this.#detail.load({ shopId: this.#shopId, cardId: this.cardId });
  }
}
